import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import currencyRatesData from '../services/currencyRates.json';
import { fetchLatestRates } from '../services/ratesAPI';
import { fetchLatestRatesWithMarkups } from '../services/markupsAPI';
import { formatNumber, resolveProductHidden, withKey, API_BASE } from '../services/utils';
import { useOfferState, isRowNonEmpty } from '../hooks/useOfferState';
import { useAutoSave } from '../hooks/useAutoSave';
import { useZohoLookups } from '../hooks/useZohoLookups';
import { useProductsVendors, isUntouchedStarterRow } from '../hooks/useProductsVendors';
import ItemsSection from './ItemsSection';
import TotalsSection from './TotalsSection';
import ExchangeRateModal from './ExchangeRateModal';
import SaveStatus from './SaveStatus';
import SyncSave from './SyncSave';
import CopyFromOfferModal from './CopyFromOfferModal';
import { fetchOfferItemsById } from '../services/zohoAPI';
import '../styles/OfferBuilder.css';

const OfferBuilder = ({ adminEdit = false }) => {
  const [currencyRates, setCurrencyRates] = useState(currencyRatesData);
  const [currentRates, setCurrentRates] = useState(null);
  const [invoiceCurrency, setInvoiceCurrency] = useState('DKK');
  const [showExchangeRate, setShowExchangeRate] = useState(false);
  const [showCopyOfferModal, setShowCopyOfferModal] = useState(false);
  const [isCopyingOfferItems, setIsCopyingOfferItems] = useState(false);

  // Detect new-offer-from-deal flow (deal_id in URL, no offer_id)
  const isNewDealFlow = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return !!params.get('deal_id') && !params.get('offer_id');
  }, []);

  const { offer, setOffer, emptyItem, normalizeLoadedOffer } = useOfferState({
    setInvoiceCurrency,
    setCurrencyRates
  });

  const isExistingOfferWithSnapshot = !!offer?.id && !!offer?.fx_rates && Object.keys(offer.fx_rates).length > 0;
  const isReadOnly = adminEdit
    ? false
    : /issued|credited/.test(String(offer?.status || '').toLowerCase());

  const {
    products,
    vendors,
    showProductDropdown,
    showVendorDropdown,
    searchingProduct,
    searchingVendor,
    setShowProductDropdown,
    setShowVendorDropdown,
    filterProducts,
    filterVendors,
    fetchAllProducts,
    fetchVendorsForProduct,
    selectProduct,
    selectVendor,
    pendingSupplierQueue,
    startSupplierSelectionFlow,
    advanceSupplierQueue
  } = useProductsVendors({ offer, setOffer, emptyItem });

  useZohoLookups({ setOffer, normalizeLoadedOffer });

  const offerWithCalcRef = useRef(null);
  const totalCurrencyRef = useRef('DKK');
  const totalAmountInCurrencyRef = useRef(0);
  const { isSavingZoho, lastSaveTime, syncToZoho, performInitialSave, hasInitialSave, flushPendingUpdate } = useAutoSave({
    offer,
    offerWithCalcRef,
    setOffer,
    normalizeLoadedOffer,
    invoiceCurrency,
    currencyRates,
    currentRates,
    isNewDealFlow,
    totalCurrencyRef,
    totalAmountInCurrencyRef,
  });

  // Column size, when first loading in
  const tableRef = useRef(null);
  const [columnWidths, setColumnWidths] = useState({
    'Drag': 20,
    '#': 17,
    'Product': 100,
    'Description': 150,
    'Supplier': 100,
    'Units': 40,
    'UnitCost': 75,
    'TotalCost': 75,
    'Markup(%)': 65,
    'UnitPrice': 75,
    'TotalPrice': 75,
    'LocalCost': 75,
    'CostCur': 45,
    'Profit': 60,
    'Profit(%)': 60,
    'VAT': 30,
    'VAT Price': 65,
    'TotalPrice+VAT': 85,
    'Action': 30,
    'Hidden': 30
  });

  // Adjusted column size, when clicking into each field.
  const focusExpandWidths = useMemo(() => ({
    'Description': 400,
    'Units': 80,
    'UnitCost': 170,
    'TotalCost': 170,
    'Markup(%)': 120,
    'UnitPrice': 170,
    'TotalPrice': 170,
    'LocalCost': 170,
    'Profit': 120,
    'Profit(%)': 100,
    'VAT': 60,
    'VAT Price': 120,
    'TotalPrice+VAT': 170
  }), []);

  // Track if the column is clicked on
  const [activeColumn, setActiveColumn] = useState(null);
  // Looks at picked column, gets adjusted column size.
  // Calculates how many pixels it needed the column needs, and equally shrinks all other columns
  const getEffectiveColumnWidths = useCallback(() => {
    if (!activeColumn || !focusExpandWidths[activeColumn]) {
      return columnWidths;
    }

    const expandedWidth = focusExpandWidths[activeColumn];
    const currentWidth = columnWidths[activeColumn];
    const widthIncrease = expandedWidth - currentWidth;

    if (widthIncrease <= 0) return columnWidths;

    // Calculate how many pixels column expands. Pixels / no. columns. Max shrink to 30px
    const shrinkableColumns = Object.keys(columnWidths).filter(
      col => col !== activeColumn && col !== '#' && col !== 'Action' && col !== 'Hidden' && col !== 'Drag'
    );
    const shrinkPerColumn = widthIncrease / shrinkableColumns.length;

    const newWidths = { ...columnWidths };
    newWidths[activeColumn] = expandedWidth;

    shrinkableColumns.forEach(col => {
      newWidths[col] = Math.max(30, columnWidths[col] - shrinkPerColumn);
    });

    return newWidths;

  }, [activeColumn, columnWidths, focusExpandWidths]);

  const effectiveWidths = getEffectiveColumnWidths();

  const ratesInitRef = useRef(false);
  const resizingRef = useRef({ isResizing: false, columnKey: null, startX: 0, startWidth: 0 });

  // When Mouse on column edge, set resizing state for column. Make cursor column resize.
  const handleMouseDown = useCallback((e, columnKey) => {
    e.preventDefault();
    resizingRef.current = {
      isResizing: true,
      columnKey,
      startX: e.clientX,
      startWidth: columnWidths[columnKey]
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  // Mouse moves the columns, calculate how much and set as new size.
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizingRef.current.isResizing) return;
      
      const { columnKey, startX, startWidth } = resizingRef.current;
      const diff = e.clientX - startX;
      const newWidth = Math.max(30, startWidth + diff);
      
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: newWidth
      }));
    };

    const handleMouseUp = () => {
      if (resizingRef.current.isResizing) {
        resizingRef.current.isResizing = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Calcualte local cost based on unit cost and exchange rate.
  const calculatedData = useMemo(() => {
    // Round to øre (2 dp, half up). Every per-row money value is rounded so
    // totals are sums of rounded lines — keeps UI, local DB and Zoho in
    // agreement and the invariant Net + VAT = Total exact at 2 dp.
    const round2 = (n) => Math.round(n * 100) / 100;
    const getRowNumbers = (item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitCost = item.unit_cost === '' ? 0 : (parseFloat(item.unit_cost) || 0);
      const baseUnitPrice = item.unit_price === '' ? 0 : (parseFloat(item.unit_price) || 0);
      const vatChecked = parseFloat(item.vat) > 0;

      // Compute base totals (without any adjustment)
      const baseTotalPrice = baseUnitPrice * quantity;
      const baseTotalCost = unitCost * quantity;
      const baseProfit = baseTotalPrice - baseTotalCost;
      const baseVatPrice = vatChecked ? baseProfit * 0.25 : 0;
      const baseTotalPriceVat = baseTotalPrice + baseVatPrice;

      // Evaluate stored expression against base (expr is always relative, e.g. "+300")
      const expr = (item.total_vat_adjustment_expr || '').trim();
      let adjustmentTotalVat = 0;
      if (expr) {
        try {
          const full = `${baseTotalPriceVat}${expr}`;
          const normalized = full.replace(/\d[\d.,]*/g, t =>
            t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t
          );
          if (/^[0-9+\-*/().\s]+$/.test(normalized)) {
            // eslint-disable-next-line no-eval
            const target = eval(normalized);
            if (!isNaN(target)) {
              adjustmentTotalVat = target - baseTotalPriceVat;
            }
          }
        } catch (e) {}
      }

      const adjustmentTotalPrice = vatChecked ? (adjustmentTotalVat / 1.25) : adjustmentTotalVat;
      const adjustmentPerUnit = quantity !== 0 ? (adjustmentTotalPrice / quantity) : 0;
      const effectiveUnitPrice = baseUnitPrice + adjustmentPerUnit;
      return {
        quantity,
        unitCost,
        baseUnitPrice,
        vatChecked,
        adjustmentTotalVat,
        adjustmentTotalPrice,
        adjustmentPerUnit,
        effectiveUnitPrice,
        baseTotalPriceVat,
      };
    };

    const calculatedItems = offer.items.map(item => {
      const {
        quantity,
        unitCost,
        vatChecked,
        baseUnitPrice,
        adjustmentPerUnit,
        adjustmentTotalVat,
        adjustmentTotalPrice,
        effectiveUnitPrice,
        baseTotalPriceVat,
      } = getRowNumbers(item);
      const markupPercent = parseFloat(item.markup_percent) || 0;
      // Local cost. Convert unit cost to selected currency
      let localCost = '';
      if (item.costcurr && item.costcurr !== 'DKK' && currencyRates[item.costcurr]) {
        localCost = (item.unit_cost / (currencyRates[item.costcurr] / 100)).toFixed(2) * 1;
      } else {
        localCost = (item.unit_cost !== '' && !isNaN(item.unit_cost)) ? Number(item.unit_cost).toFixed(2) * 1 : item.unit_cost;
      }
      // Only use fallback if unit_price is not an empty string
      let baseUnitPriceResolved;
      if (item.unit_price === '') {
        baseUnitPriceResolved = 0;
      } else if (isNaN(item.unit_price)) {
        baseUnitPriceResolved = 0;
      } else if (item.unit_price !== undefined) {
        baseUnitPriceResolved = baseUnitPrice || (unitCost * (1 + markupPercent / 100));
      } else {
        baseUnitPriceResolved = unitCost * (1 + markupPercent / 100);
      }
      const totalCost = unitCost * quantity;
      // Apply adjustment directly at the total level to avoid divide-then-multiply
      // rounding errors (e.g. +0.20 / 6 * 6 ≠ 0.20 in floating point)
      const totalPrice = baseUnitPriceResolved * quantity + adjustmentTotalPrice;
      // Derive display unit price from the exact total
      const unitPrice = (adjustmentTotalPrice !== 0 && quantity !== 0)
        ? totalPrice / quantity
        : baseUnitPriceResolved;
      const profit = totalPrice - totalCost;
      //VAT / MOMS
      const vatRate = vatChecked ? 0.25 : 0;
      const totalPriceVat = adjustmentTotalVat !== 0
        ? baseTotalPriceVat + adjustmentTotalVat
        : totalPrice + (vatChecked ? profit * vatRate : 0);
      // Round the row to øre once, then derive the rest from the rounded
      // values so each row is internally consistent at 2 dp (a raw half-øre
      // like vat 921.875 would otherwise round differently here vs backend).
      const rTotalCost = round2(totalCost);
      const rTotalPrice = round2(totalPrice);
      const rTotalPriceVat = round2(totalPriceVat);
      // Derive vatPrice from totalPriceVat to keep Net + VAT = Total consistent
      const rVatPrice = round2(rTotalPriceVat - rTotalPrice);
      const rProfit = round2(rTotalPrice - rTotalCost);
      const profitPercent = rTotalPrice !== 0 ? (rProfit / Math.abs(rTotalPrice)) * 100 : 0;
      return {
        ...item,
        unit_price_base: baseUnitPrice,
        adjustment_per_unit: adjustmentPerUnit,
        base_total_price_vat: baseTotalPriceVat,
        total_vat_adjustment_value: Number(adjustmentTotalVat.toFixed(4)),
        unit_price: item.unit_price === '' ? '' : unitPrice,
        total_cost: rTotalCost,
        total_price: rTotalPrice,
        profit: rProfit,
        profit_percent: profitPercent,
        vat_price: rVatPrice,
        total_price_vat: rTotalPriceVat,
        local_cost: localCost
      };
    });
    // Per-row values are 2 dp; round2 the sums to clean float accumulation noise.
    const net = round2(calculatedItems.reduce((sum, item) => sum + (item.total_price || 0), 0));
    const tax = round2(calculatedItems.reduce((sum, item) => sum + (item.vat_price || 0), 0));
    // Cost: sum of total_cost for rows where supplier is not "80DAYS"
    const externalCost = round2(calculatedItems.reduce((sum, item) => {
      const supplierName = (item.supplier_name || '').trim().toUpperCase();
      if (supplierName === '80DAYS') return sum;
      return sum + (item.total_cost || 0);
    }, 0));
    const cost = round2(calculatedItems.reduce((sum, item) => sum + (item.total_cost || 0), 0));

    return {
      items: calculatedItems,
      net,
      tax,
      externalCost,
      cost,
    };
  }, [offer.items, currencyRates]);

  // Merge calculated data with offer
  const totalCalc = Math.round((calculatedData.net + calculatedData.tax) * 100) / 100;
  const profitCalc = calculatedData.net - calculatedData.externalCost;
  const profitDgCalc = calculatedData.net !== 0 ? (profitCalc / Math.abs(calculatedData.net)) * 100 : 0;

  const offerWithCalculations = useMemo(() => ({
    ...offer,
    items: calculatedData.items,
    subtotal: calculatedData.net,
    net: calculatedData.net,
    tax: calculatedData.tax,
    total: totalCalc,
    externalCost: calculatedData.externalCost,
    cost: calculatedData.cost,
    profit: profitCalc,
    profit_dg: profitDgCalc
  }), [offer, calculatedData, totalCalc, profitCalc, profitDgCalc]);
  offerWithCalcRef.current = offerWithCalculations;

  // Fetch latest rates on mount. If already saved snapshot in DB, use them instead.
  // ratesInitRef prevents re-initialization after manual "Pull New Exchange Rates" updates.
  useEffect(() => {
    let cancelled = false;
    console.log('[FX-DEBUG] initRates useEffect triggered. ratesInitRef:', ratesInitRef.current, ', isExistingOfferWithSnapshot:', isExistingOfferWithSnapshot, ', offer.fx_rates EUR:', offer?.fx_rates?.EUR);
    async function initRates() {
      try {
        // Fetch raw rates for the "current rate" column, and marked-up rates for new offers
        const latest = await fetchLatestRates();
        if (cancelled) { console.log('[FX-DEBUG] initRates cancelled (stale)'); return; }
        console.log('[FX-DEBUG] initRates fetched latest. EUR:', latest?.EUR);
        setCurrentRates(latest);

        // Only initialize currencyRates once. After that, manual updates
        // (Pull New Exchange Rates) and normalizeLoadedOffer handle it.
        if (!ratesInitRef.current) {
          console.log('[FX-DEBUG] initRates: first init, setting currencyRates');
          if (isExistingOfferWithSnapshot) {
            console.log('[FX-DEBUG] initRates: using offer snapshot. EUR:', offer.fx_rates?.EUR);
            setCurrencyRates(offer.fx_rates);
          } else {
            // For new offers, use rates with global markups applied
            console.log('[FX-DEBUG] initRates: no snapshot, fetching marked-up rates');
            try {
              const markedUpData = await fetchLatestRatesWithMarkups();
              if (cancelled) return;
              const markedUpRates = markedUpData?.marked_up_rates || latest;
              setCurrencyRates(markedUpRates);
              setOffer(prev => ({
                ...prev,
                fx_rates: markedUpRates,
                fx_source: "Nationalbanken",
                fx_fetched_at: new Date().toISOString()
              }));
            } catch (markupErr) {
              console.warn('[FX-DEBUG] Failed to fetch marked-up rates, falling back to raw:', markupErr);
              setCurrencyRates(latest);
              setOffer(prev => ({
                ...prev,
                fx_rates: latest,
                fx_source: "Nationalbanken",
                fx_fetched_at: new Date().toISOString()
              }));
            }
          }
          ratesInitRef.current = true;
        } else {
          console.log('[FX-DEBUG] initRates: SKIPPING (ratesInitRef is already true)');
        }
      } catch (e) {
        console.error('[FX-DEBUG] initRates error:', e);
      }
    }

    initRates();
    return () => { cancelled = true; };
  }, [isExistingOfferWithSnapshot, offer.fx_rates, setOffer]);

  // Parse and convert currency to dkk. Ntioanlbanken shows per 100 dkk, so we divide by 100 for non dkk.
  const parseCurrencyValue = (str, rates = currencyRates) => {
    const match = str.trim().match(/^([0-9.,]+)\s*([A-Za-z]{3})?$/);
    if (match) {
      let val = parseFloat(match[1].replace(',', '.'));
      let curr = (match[2] || 'DKK').toUpperCase();
      if (!rates[curr]) return null;
      return curr === 'DKK' ? val : val * (rates[curr] / 100);
    }
    return null;
  };

  // Evaluate a math expression with currencies, returning DKK
  const safeEval = (expr, rates = currencyRates) => {
    try {
      const normalizeExpressionNumbers = (value) =>
        value.replace(/\d[\d.,]*/g, token => {
          if (token.includes(',')) {
            return token.replace(/\./g, '').replace(',', '.');
          }
          return token;
        });

      let replaced = expr.replace(/([0-9.,]+)\s*([A-Za-z]{3})/g, (m, num, curr) => {
        const dkk = parseCurrencyValue(m, rates);
        return dkk !== null ? dkk.toString() : m;
      });
      replaced = normalizeExpressionNumbers(replaced);
      if (/^[0-9+\-*/().\s]+$/.test(replaced)) {
        // eslint-disable-next-line no-eval
        return eval(replaced);
      }
    } catch (e) {}
    return expr;
  };

  // Re-evaluate items' currency-bearing _expr fields against newRates and
  // cascade the changes through markup/price math. Mirrors handleItemChange's
  // cascade logic for the unit_cost / unit_price / total_price_vat fields.
  // Items without currency codes in any _expr are returned unchanged —
  // their local_cost still updates via the calculatedData memo when
  // currencyRates change.
  const recalculateItemsForNewRates = (items, newRates) => {
    const hasCurrency = (s) => /[A-Za-z]{3}/.test(s || '');
    return items.map(item => {
      const ucExpr = (item.unit_cost_expr || '').toString();
      const upExpr = (item.unit_price_expr || '').toString();
      const tpvExpr = (item.total_price_vat_expr || '').toString();
      const ucHasCurr = hasCurrency(ucExpr);
      const upHasCurr = hasCurrency(upExpr);
      const tpvHasCurr = hasCurrency(tpvExpr);

      if (!ucHasCurr && !upHasCurr && !tpvHasCurr) return item;

      const next = { ...item };

      // 1) unit_cost_expr with currency → re-eval unit_cost, cascade to markup or unit_price.
      if (ucHasCurr) {
        const evalValue = safeEval(ucExpr, newRates);
        const numVal = Number(evalValue);
        if (!Number.isNaN(numVal) && evalValue !== '') {
          next.unit_cost = numVal;
          const m = ucExpr.trim().match(/([0-9.,]+)\s*([A-Za-z]{3})/);
          if (m && m[2]) next.costcurr = m[2].toUpperCase();

          const newUnitCost = parseFloat(next.unit_cost) || 0;
          const existingPrice = next.unit_price;
          const existingPriceNum = (existingPrice === '' || existingPrice === undefined)
            ? 0
            : (parseFloat(existingPrice) || 0);
          const priceIsExplicit = existingPrice !== '' && existingPrice !== undefined && next.unit_price_locked;
          if (existingPriceNum > 0 || priceIsExplicit) {
            if (newUnitCost > 0) {
              next.markup_percent = (((existingPriceNum / newUnitCost) - 1) * 100).toFixed(2);
            }
          } else {
            const markup = parseFloat(next.markup_percent) || 0;
            if (markup > 0) {
              next.unit_price = (newUnitCost * (1 + markup / 100)).toFixed(2) * 1;
              next.unit_price_expr = next.unit_price.toString();
            }
          }
        }
      }

      // 2) unit_price_expr with currency → re-eval unit_price, cascade to markup.
      if (upHasCurr) {
        const evalValue = safeEval(upExpr, newRates);
        const numVal = Number(evalValue);
        if (!Number.isNaN(numVal) && evalValue !== '') {
          next.unit_price = numVal;
          const unitCost = next.unit_cost === '' ? 0 : parseFloat(next.unit_cost) || 0;
          if (unitCost > 0 && next.unit_price !== '' && !Number.isNaN(next.unit_price)) {
            next.unit_price_locked = true;
            next.markup_percent = (((next.unit_price / unitCost) - 1) * 100).toFixed(2);
          }
        }
      }

      // 3) total_price_vat_expr with currency → re-eval target, back-calc unit_price, cascade markup.
      if (tpvHasCurr) {
        const quantity = parseFloat(next.quantity) || 0;
        if (quantity > 0) {
          const targetTotalVat = Number(safeEval(tpvExpr, newRates));
          if (!Number.isNaN(targetTotalVat)) {
            const unitCost = next.unit_cost === '' ? 0 : (parseFloat(next.unit_cost) || 0);
            const vatEnabled = parseFloat(next.vat) > 0;
            const vatRate = vatEnabled ? 0.25 : 0;
            const divisor = quantity * (1 + vatRate);
            const newUnitPrice = Number(((targetTotalVat + unitCost * quantity * vatRate) / divisor).toFixed(2));
            next.unit_price = newUnitPrice;
            next.unit_price_expr = newUnitPrice.toString();
            next.unit_price_locked = true;
            next.markup_percent = unitCost > 0
              ? (((newUnitPrice / unitCost) - 1) * 100).toFixed(2)
              : 0;
          }
        }
      }

      return next;
    });
  };

  const handleItemChange = (index, field, value) => {
    setOffer(prev => {
      if (isReadOnly) {
        return prev;
      }
      const newItems = [...prev.items];
      // Handling of unit_cost and unit_price to support math operators and curr codes
      if (field === 'unit_cost') {
        newItems[index].unit_cost_expr = value;
        if (value === '') {
          newItems[index].unit_cost = '';
          newItems[index].costcurr = '';
        } else {
          const match = value.trim().match(/([0-9.,]+)\s*([A-Za-z]{3})/);
          if (match && match[2]) {
            newItems[index].costcurr = match[2].toUpperCase();
          } else if (!newItems[index].costcurr) {
            newItems[index].costcurr = 'DKK';
          }
          const evalValue = safeEval(value);
          const numVal = Number(evalValue);
          newItems[index].unit_cost = (isNaN(numVal) || evalValue === '') ? '' : numVal;
        }
        // If a unit_price is already set, keep it and recalculate markup.
        // Only drive unit_price from markup when there is already a markup set.
        if (newItems[index].unit_cost !== '' && !isNaN(newItems[index].unit_cost)) {
          const newUnitCost = parseFloat(newItems[index].unit_cost) || 0;
          const existingPrice = newItems[index].unit_price;
          const existingPriceNum = (existingPrice === '' || existingPrice === undefined) ? 0 : (parseFloat(existingPrice) || 0);
          const priceIsExplicit = existingPrice !== '' && existingPrice !== undefined && newItems[index].unit_price_locked;
          if (existingPriceNum > 0 || priceIsExplicit) {
            // UnitPrice already set (including explicitly 0) — keep it, recalculate markup
            if (newUnitCost > 0) {
              newItems[index].markup_percent = (((existingPriceNum / newUnitCost) - 1) * 100).toFixed(2);
            }
          } else {
            // No UnitPrice yet — only derive from cost+markup if markup is actually set
            const markup = parseFloat(newItems[index].markup_percent) || 0;
            if (markup > 0) {
              newItems[index].unit_price = (newUnitCost * (1 + markup / 100)).toFixed(2) * 1;
              newItems[index].unit_price_expr = newItems[index].unit_price.toString();
            }
          }
        }
      } else if (field === 'markup_percent') {
        // Treat markup as direct percent input (e.g. 5 => +5%).
        // Editing markup always drives unit_price (last-edited-field wins).
        let raw = value.replace(',', '.');
        newItems[index][field] = value;
        if (/^\d+[.,]?\d*$/.test(value) && value !== '' && value !== '.' && value !== ',') {
          const markup = parseFloat(raw);
          if (!isNaN(markup)) {
            const unitCost = newItems[index].unit_cost === '' ? 0 : parseFloat(newItems[index].unit_cost) || 0;
            newItems[index].unit_price = (unitCost * (1 + markup / 100)).toFixed(2) * 1;
            newItems[index].unit_price_expr = newItems[index].unit_price.toString();
            // Unlock price so future cost edits recalculate via markup
            newItems[index].unit_price_locked = false;
            // Clear any TotalPrice+VAT adjustment — markup is now the driver
            newItems[index].total_vat_adjustment_expr = '';
            newItems[index].total_vat_adjustment_value = 0;
          }
        }
      } else if (field === 'unit_price') {
        // Clear any TotalPrice+VAT adjustment when unit price is manually edited
        newItems[index].total_vat_adjustment_value = 0;
        newItems[index].total_vat_adjustment_expr = '';
        newItems[index].unit_price_expr = value;
        let evalValue;
        if (value === '') {
          newItems[index].unit_price = '';
        } else {
          evalValue = safeEval(value);
          const numVal = Number(evalValue);
          newItems[index].unit_price = (isNaN(numVal) || evalValue === '') ? '' : numVal;
        }
        // Update markup_percent to match new unit price
        const unitCost = newItems[index].unit_cost === '' ? 0 : parseFloat(newItems[index].unit_cost) || 0;
        if (unitCost > 0 && newItems[index].unit_price !== '' && !isNaN(newItems[index].unit_price)) {
          // Mark unit price as locked only if we can calculate markup
          newItems[index].unit_price_locked = true;
          newItems[index].markup_percent = (((newItems[index].unit_price / unitCost) - 1) * 100).toFixed(2);
        } else if (newItems[index].unit_price !== '' && !isNaN(newItems[index].unit_price) && newItems[index].unit_price > 0) {
          // No unit_cost — just lock the price so a later cost entry won't override it
          newItems[index].markup_percent = 0;
          newItems[index].unit_price_locked = true;
        } else {
          newItems[index].unit_price_locked = false;
        }
      } else if (field === 'total_price_vat') {
        const exprInput = (value ?? '').toString().trim();
        if (exprInput === '') {
          newItems[index].total_vat_adjustment_expr = '';
          return { ...prev, items: newItems };
        }

        const quantity = parseFloat(newItems[index].quantity) || 0;
        if (quantity === 0) return { ...prev, items: newItems };

        const unitCost = newItems[index].unit_cost === '' ? 0 : (parseFloat(newItems[index].unit_cost) || 0);
        const baseUnitPrice = newItems[index].unit_price === '' ? 0 : (parseFloat(newItems[index].unit_price) || 0);
        const vatEnabled = parseFloat(newItems[index].vat) > 0;

        const baseTotalPrice = baseUnitPrice * quantity;
        const baseProfit = baseTotalPrice - (unitCost * quantity);
        const baseTotalPriceVat = baseTotalPrice + (vatEnabled ? baseProfit * 0.25 : 0);

        // Classify input: relative (e.g. "+300", "-200+50") vs absolute (e.g. "59000", "-39.1").
        // Leading minus is relative only when there is another operator after the sign.
        let isRelativeExpr = /^[+*/]/.test(exprInput);
        if (!isRelativeExpr && /^-/.test(exprInput)) {
          isRelativeExpr = /[+\-*/]/.test(exprInput.slice(1));
        }
        const targetTotalVat = Number(safeEval(isRelativeExpr ? `${baseTotalPriceVat}${exprInput}` : exprInput));
        if (Number.isNaN(targetTotalVat)) return { ...prev, items: newItems };

        const isPlainZero = targetTotalVat === 0 && /^[0.,]+$/.test(exprInput);

        // Plain "0" on a priced row → clear the price entirely
        if (isPlainZero && baseUnitPrice !== 0) {
          newItems[index].unit_price = '';
          newItems[index].unit_price_expr = '';
          newItems[index].unit_price_locked = false;
          newItems[index].total_vat_adjustment_expr = '';
          return { ...prev, items: newItems };
        }

        // Always back-calculate unit_price from the target total.
        // TotalPriceVat = unitPrice*qty + (unitPrice*qty - unitCost*qty) * vatRate
        // Solving for unitPrice: (target + unitCost*qty*vatRate) / (qty * (1 + vatRate))
        const vatRate = vatEnabled ? 0.25 : 0;
        const divisor = quantity * (1 + vatRate);
        const newUnitPrice = Number(((targetTotalVat + unitCost * quantity * vatRate) / divisor).toFixed(2));
        newItems[index].unit_price = newUnitPrice;
        // Store expression on whichever field it actually matches
        const isComplexExpr = /[*/+]/.test(exprInput) || /\d-/.test(exprInput) || /[A-Za-z]{3}/.test(exprInput);
        if (isComplexExpr && !isPlainZero && exprInput !== '') {
          if (Math.abs(targetTotalVat - newUnitPrice) < 0.01) {
            // Expression matches unitPrice (no VAT/qty distortion): show on UnitPrice hover
            newItems[index].unit_price_expr = exprInput;
            newItems[index].total_price_vat_expr = '';
          } else {
            // Expression matches totalPriceVat but not unitPrice: show on TotalPrice+VAT hover
            newItems[index].unit_price_expr = newUnitPrice.toString();
            newItems[index].total_price_vat_expr = exprInput;
          }
        } else {
          newItems[index].unit_price_expr = newUnitPrice.toString();
          newItems[index].total_price_vat_expr = '';
        }
        newItems[index].unit_price_locked = true;
        newItems[index].total_vat_adjustment_expr = '';
        if (!newItems[index].costcurr) newItems[index].costcurr = 'DKK';
        newItems[index].markup_percent = unitCost > 0
          ? (((newUnitPrice / unitCost) - 1) * 100).toFixed(2)
          : 0;
      } else if (field === 'quantity') {
        newItems[index].quantity = value;
        // Validate stored expressions — clear if they no longer match their target value
        const upExpr = newItems[index].unit_price_expr || '';
        const isUpComplex = /[*/+]/.test(upExpr) || /\d-/.test(upExpr) || /[A-Za-z]{3}/.test(upExpr);
        if (isUpComplex) {
          const evaluated = Number(safeEval(upExpr));
          const unitPrice = parseFloat(newItems[index].unit_price) || 0;
          if (isNaN(evaluated) || Math.abs(evaluated - unitPrice) > 0.01) {
            newItems[index].unit_price_expr = (newItems[index].unit_price ?? '').toString();
          }
        }
        const tpvExpr = newItems[index].total_price_vat_expr || '';
        const isTpvComplex = /[*/+]/.test(tpvExpr) || /\d-/.test(tpvExpr) || /[A-Za-z]{3}/.test(tpvExpr);
        if (isTpvComplex) {
          // total_price_vat will be recalculated; can't validate yet — clear it
          newItems[index].total_price_vat_expr = '';
        }
      } else {
        newItems[index][field] = value;
      }
      // Calculate total for this item
      if (field === 'quantity' || field === 'unit_price' || field === 'total_price_vat') {
        const quantity = parseFloat(newItems[index].quantity) || 0;
        const unitPrice = parseFloat(newItems[index].unit_price) || 0;
        newItems[index].total = quantity * unitPrice;
      }
      return { ...prev, items: newItems };
    });
  };

  const addRowToTop = () => {
    setOffer(prev => {
      if (isReadOnly) return prev;
      const newId = prev.items.length > 0 ? Math.max(...prev.items.map(i => i.id)) + 1 : 1;
      return { ...prev, items: [emptyItem(newId), ...prev.items] };
    });
  };

  const addRowToBottom = () => {
    setOffer(prev => {
      if (isReadOnly) return prev;
      const newId = prev.items.length > 0 ? Math.max(...prev.items.map(i => i.id)) + 1 : 1;
      return { ...prev, items: [...prev.items, emptyItem(newId)] };
    });
  };

  const moveRow = useCallback((fromIndex, toIndex) => {
    setOffer(prev => {
      if (isReadOnly) return prev;
      if (fromIndex === toIndex) return prev;
      const newItems = [...prev.items];
      const [moved] = newItems.splice(fromIndex, 1);
      newItems.splice(toIndex, 0, moved);
      return { ...prev, items: newItems };
    });
  }, [isReadOnly, setOffer]);

  //delete row if more than 1 exists, and if not the last with a product
  const deleteRow = (index) => {
    setOffer(prev => {
      if (isReadOnly) {
        return prev;
      }
      const nonEmptyRows = prev.items.filter(item => isRowNonEmpty(item)).length;
      const itemToDelete = prev.items[index];
      
      if (isRowNonEmpty(itemToDelete) && nonEmptyRows <= 1) {
        return prev;
      }
      if (prev.items.length <= 1) return prev;
      
      const newItems = prev.items.filter((_, i) => i !== index);
      return { ...prev, items: newItems };
    });
  };

  const toggleHiddenRow = useCallback((index) => {
    setOffer(prev => {
      if (isReadOnly) {
        return prev;
      }
      const newItems = [...prev.items];
      const current = newItems[index];
      if (!current) return prev;
      newItems[index] = { ...current, hidden: !current.hidden };
      return { ...prev, items: newItems };
    });
  }, [isReadOnly, setOffer]);

  const handleCopyFromExistingOffer = useCallback(async (selectedOffer) => {
    if (!selectedOffer?.id) return;

    const confirmed = window.confirm(
      `Add line items from "${selectedOffer.name || selectedOffer.id}" to current offer?`
    );
    if (!confirmed) return;

    setIsCopyingOfferItems(true);
    try {
      const imported = await fetchOfferItemsById(selectedOffer.id);
      // Copy every line, 1:1 — same lines, same order, nothing added, nothing
      // dropped. There used to be a filter here that removed the starter
      // products (OKORREKT/OMARK/OFBH) from the copy; it keyed off hardcoded
      // Zoho ids that had gone stale, so it silently did nothing anyway.
      const importedItems = imported?.items || [];
      if (importedItems.length === 0) {
        window.alert('Selected offer has no line items to copy.');
        return;
      }

      // A copy is appended after the rows the user has already built. Blank
      // rows are dropped. If the offer holds no real work yet (only untouched
      // starter rows), the copy replaces them, so the result is exactly the
      // source offer instead of the source plus a duplicate set of starter
      // rows. That duplication is what left offer 49547 with three OFBH lines.
      const keptItems = offer.items.filter(isRowNonEmpty);
      const replaceAll = keptItems.every(isUntouchedStarterRow);
      const maxId = replaceAll
        ? 0
        : Math.max(0, ...keptItems.map(i => Number(i.id) || 0));

      let normalizedItems = importedItems.map((item, idx) => ({
        ...emptyItem(maxId + idx + 1),
        ...item,
        id: maxId + idx + 1,
        // The copies are new rows in this offer, so they must not inherit the
        // source's Zoho subform row ids — that would make saving overwrite the
        // source offer's lines.
        zoho_line_item_id: null,
        unit_cost_expr: item?.unit_cost_expr ?? (item?.unit_cost !== undefined ? String(item.unit_cost) : ''),
        unit_price_expr: item?.unit_price_expr ?? (item?.unit_price !== undefined ? String(item.unit_price) : ''),
        hidden: !!item?.hidden,
      }));

      normalizedItems = await resolveProductHidden(normalizedItems);

      setOffer(prev => ({
        ...prev,
        items: replaceAll ? normalizedItems : [...keptItems, ...normalizedItems],
      }));
      setShowCopyOfferModal(false);

      // Only rows that arrived without a supplier need the selection flow; a
      // copied supplier is kept as-is. The copied rows start after the kept ones.
      startSupplierSelectionFlow(normalizedItems, replaceAll ? 0 : keptItems.length);
    } catch (error) {
      console.error('Failed to copy line items from existing offer:', error);
      window.alert('Failed to copy line items from selected offer.');
    } finally {
      setIsCopyingOfferItems(false);
    }
  }, [emptyItem, offer.items, setOffer, startSupplierSelectionFlow]);

  return (
    <div className="offer-builder">

      <ItemsSection
        offerWithCalculations={offerWithCalculations}
        offer={offer}
        tableRef={tableRef}
        effectiveWidths={effectiveWidths}
        handleMouseDown={handleMouseDown}
        handleItemChange={handleItemChange}
        deleteRow={deleteRow}
        toggleHiddenRow={toggleHiddenRow}
        moveRow={moveRow}
        addRowToTop={addRowToTop}
        addRowToBottom={addRowToBottom}
        flushPendingUpdate={flushPendingUpdate}
        products={products}
        vendors={vendors}
        showProductDropdown={showProductDropdown}
        showVendorDropdown={showVendorDropdown}
        searchingProduct={searchingProduct}
        searchingVendor={searchingVendor}
        setShowProductDropdown={setShowProductDropdown}
        setShowVendorDropdown={setShowVendorDropdown}
        filterProducts={filterProducts}
        filterVendors={filterVendors}
        fetchAllProducts={fetchAllProducts}
        fetchVendorsForProduct={fetchVendorsForProduct}
        selectProduct={selectProduct}
        selectVendor={selectVendor}
        pendingSupplierQueue={pendingSupplierQueue}
        advanceSupplierQueue={advanceSupplierQueue}
        setActiveColumn={setActiveColumn}
        activeColumn={activeColumn}
        formatNumber={formatNumber}
        isReadOnly={isReadOnly}
      />

      <div className="px-4">
        <TotalsSection
          isReadOnly={isReadOnly}
          cost={offerWithCalculations?.cost || 0}
          net={offerWithCalculations?.net || 0}
          vat={offerWithCalculations?.tax || 0}
          fxFetchedAt={offer.fx_fetched_at}
          onCurrencyChange={(cur, amount) => {
            totalCurrencyRef.current = cur;
            totalAmountInCurrencyRef.current = cur === 'DKK' ? 0 : amount;
          }}
          onAdjustTotal={async (diffDKK) => {
            const round2 = (n) => Math.round(n * 100) / 100;
            const isOkorrekt = (item) => (item?.product || '').trim().toUpperCase() === 'OKORREKT';

            const existingIdx = offer.items.findIndex(isOkorrekt);
            if (existingIdx >= 0) {
              setOffer(prev => {
                const items = [...prev.items];
                const existing = items[existingIdx];
                const currentPrice = parseFloat(existing.unit_price) || 0;
                items[existingIdx] = {
                  ...existing,
                  unit_price: round2(currentPrice + diffDKK),
                  unit_price_locked: true,
                };
                return { ...prev, items };
              });
              return;
            }

            // No OKORREKT line yet — resolve the product fresh from the cache so
            // we always pick up the current Zoho id + supplier (the previous
            // hardcoded id went stale and broke Zoho sync).
            let product = null;
            let suppliers = [];
            try {
              const prodRes = await fetch(await withKey(`${API_BASE}/api/zoho/products?search=OKORREKT`));
              const prodData = await prodRes.json();
              product = (prodData.products || []).find(p =>
                (p.code || p.Product_Code || '').trim().toUpperCase() === 'OKORREKT'
              );
              if (!product) {
                console.warn('[onAdjustTotal] OKORREKT product not found in cache; skipping auto-line. Run a bulk sync.');
                return;
              }
              const supRes = await fetch(await withKey(`${API_BASE}/api/zoho/product_suppliers?product_id=${encodeURIComponent(product.id)}`));
              const supData = await supRes.json();
              suppliers = supData.suppliers || [];
            } catch (err) {
              console.error('[onAdjustTotal] failed to resolve OKORREKT product/supplier:', err);
              return;
            }

            const supplier = suppliers.length === 1 ? suppliers[0] : null;

            setOffer(prev => {
              const items = [...prev.items];
              const newId = items.length > 0 ? Math.max(...items.map(i => i.id || 0)) + 1 : 1;
              items.push({
                ...emptyItem(newId),
                product: product.code || product.Product_Code || 'OKORREKT',
                product_id: product.id,
                description: product.description || '',
                supplier_name: supplier ? (supplier.supplier_code || supplier.name) : '',
                supplier_id: supplier ? supplier.id : null,
                unit_price: round2(diffDKK),
                unit_price_locked: true,
                vat: product.vat ? 1 : 0,
                hidden: true,
                product_hidden: !!product.hidden,
              });
              return { ...prev, items };
            });
          }}
        />
      </div>

      <div className="flex items-center justify-end gap-3 px-4">
        <button
          type="button"
          className="rounded-lg bg-company-main px-3 py-2 text-xs font-semibold text-white transition hover:brightness-95 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:brightness-100"
          onClick={() => setShowCopyOfferModal(true)}
          disabled={isReadOnly || isCopyingOfferItems}
        >
          Copy from Existing Offer
        </button>

        <button
          type="button"
          className="rounded-lg bg-company-main px-3 py-2 text-xs font-semibold text-white transition hover:brightness-95 shadow-lg"
          onClick={() => setShowExchangeRate(prev => !prev)}
          aria-expanded={showExchangeRate}
        >
          {showExchangeRate ? 'Hide exchange rate' : 'Show exchange rate'}
        </button>

        <SyncSave
          onSync={() => syncToZoho({ force: true })}
          onCreateOffer={performInitialSave}
          isSyncing={isSavingZoho}
          offerId={offer.id}
          isReadOnly={isReadOnly}
          isNewDealFlow={isNewDealFlow}
          hasInitialSave={hasInitialSave}
        />
      </div>

      <ExchangeRateModal
        show={showExchangeRate}
        onClose={() => setShowExchangeRate(false)}
        currencyRates={currencyRates}
        currentRates={currentRates}
        offer={offerWithCalculations}
        isReadOnly={isReadOnly}
        onRatesUpdated={(latestRates) => {
          console.log('[FX-DEBUG] onRatesUpdated fired, setting currentRates. EUR:', latestRates?.EUR);
          setCurrentRates(latestRates);
        }}
        onSnapshotUpdated={(fxUpdate) => {
          console.log('[FX-DEBUG] onSnapshotUpdated fired, fxUpdate keys:', fxUpdate ? Object.keys(fxUpdate) : 'null');
          if (fxUpdate) {
            console.log('[FX-DEBUG] fxUpdate.fx_rates EUR:', fxUpdate.fx_rates?.EUR, ', fx_fetched_at:', fxUpdate.fx_fetched_at);
            // Merge fx-related fields into the current offer state.
            // Re-evaluate items' currency-bearing _expr fields against the new
            // rates so DKK numerics stay consistent with the user's foreign-
            // currency intent (e.g. "300EUR" stays 300 EUR, not 297).
            const newRates = fxUpdate.fx_rates;
            setOffer(prev => {
              console.log('[FX-DEBUG] setOffer merging. prev.fx_rates EUR:', prev.fx_rates?.EUR, '-> new EUR:', fxUpdate.fx_rates?.EUR);
              const recalcedItems = newRates
                ? recalculateItemsForNewRates(prev.items, newRates)
                : prev.items.map(item => ({ ...item }));
              return {
                ...prev,
                ...fxUpdate,
                items: recalcedItems,
              };
            });
            if (newRates) {
              console.log('[FX-DEBUG] setCurrencyRates called with EUR:', newRates.EUR);
              setCurrencyRates(newRates);
            }
          }
        }}
      />

      <div className="actions px-4">
        <SaveStatus
          isSavingZoho={isSavingZoho}
          lastSaveTime={lastSaveTime}
        />

        <SyncSave
          onSync={() => syncToZoho({ force: true })}
          isSyncing={isSavingZoho}
          offerId={offer.id}
          isReadOnly={isReadOnly}
          isIconOnly={true}
        />
      </div>

      <CopyFromOfferModal
        show={showCopyOfferModal}
        onClose={() => setShowCopyOfferModal(false)}
        onConfirm={handleCopyFromExistingOffer}
        isApplying={isCopyingOfferItems}
      />

    </div>
  );
};

export default OfferBuilder;
